import { useMemo } from 'react';
import type { NetworkType } from '@mls-wallet/shared-types';
import type { AbiParameter } from 'viem';
import { useAddressBookEntriesByAddresses } from '@/hooks/organization/use-address-book-entries-by-addresses';
import {
  ParameterInput,
  type TransactionBuilderParameterState,
} from './parameter-inputs';
import { collectAddressParameterValues } from './parameter-inputs/collect-address-parameter-values';
import { cn } from '@/lib/utils';

type ContractFunctionParameterInputsProps = {
  functionAbiInputs: AbiParameter[];
  functionParameters: TransactionBuilderParameterState[];
  networkId: NetworkType;
  rawTransactionImportVersion: number;
  allowUnsavedAddress: boolean;
  cardLayout?: boolean;
  onParameterChange: (
    index: number,
    state: TransactionBuilderParameterState
  ) => void;
};

export function ContractFunctionParameterInputs({
  functionAbiInputs,
  functionParameters,
  networkId,
  rawTransactionImportVersion,
  allowUnsavedAddress,
  cardLayout = false,
  onParameterChange,
}: ContractFunctionParameterInputsProps): React.ReactElement {
  const importedAddressValues = useMemo(
    () =>
      allowUnsavedAddress
        ? collectAddressParameterValues(functionAbiInputs, functionParameters)
        : [],
    [allowUnsavedAddress, functionAbiInputs, functionParameters]
  );
  const { entryByAddress, isLoading, error } = useAddressBookEntriesByAddresses(
    importedAddressValues
  );

  if (functionAbiInputs.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        <p className="text-sm text-zinc-500 px-1">
          This function has no parameters.
        </p>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex flex-col gap-5',
        cardLayout &&
          'rounded-[24px] border border-white bg-neutral-50 p-5 shadow-[0_4px_6px_-3px_rgba(0,0,0,0.1)] dark:border-zinc-800 dark:bg-zinc-900'
      )}
    >
      {cardLayout && (
        <h3 className="border-b pb-[18px] text-base font-medium">Arguments</h3>
      )}
      {functionAbiInputs.map((parameter, index) => {
        const parameterState = functionParameters[index];

        if (!parameterState) return null;

        const input = (
          <ParameterInput
            cardLayout={cardLayout}
            name={parameter.name || '(Unnamed parameter)'}
            value={parameterState.value ?? ''}
            unformattedValue={(parameterState.parsedValue as string) || ''}
            isValid={parameterState.isValid ?? false}
            isEmpty={parameterState.isEmpty ?? true}
            networkId={networkId}
            paramAbiFragment={parameter}
            onChange={(_, newState) => onParameterChange(index, newState)}
            allowUnsavedAddress={allowUnsavedAddress}
            addressBookLookupPending={isLoading}
            addressBookLookupFailed={Boolean(error)}
            {...(allowUnsavedAddress
              ? { addressBookEntryByAddress: entryByAddress }
              : {})}
          />
        );
        return cardLayout ? (
          <fieldset
            key={`${index}:${rawTransactionImportVersion}`}
            className="min-w-0 rounded-[24px] border border-zinc-200 bg-muted dark:border-zinc-700"
          >
            <legend className="sr-only">
              {parameter.name || `Argument ${index + 1}`}
            </legend>
            <div className="flex justify-between gap-3 px-5 py-2 font-mono text-xs text-muted-foreground">
              <span>{parameter.name || `Argument ${index + 1}`}</span>
              <span>{parameter.type}</span>
            </div>
            <div className="rounded-[24px] bg-background px-5 py-2 [&_label]:sr-only [&_input]:shadow-none [&_input]:border-transparent [&_input]:focus-visible:border-ring [&_input]:rounded-xl">
              {input}
            </div>
          </fieldset>
        ) : (
          <div key={`${index}:${rawTransactionImportVersion}`}>{input}</div>
        );
      })}
    </div>
  );
}
